import { zfd, json as zfdJsonParse } from 'zod-form-data';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { mediaAssetSchema } from '~/utils/schemas';
import { registrationContext } from './registrationMiddleware';

export const AutomatedFormKeys = {
  Assets: 'assets',
} as const;

const automatedFormSchema = zfd.formData({
  [AutomatedFormKeys.Assets]: zfd.repeatableOfType(
    zfdJsonParse(mediaAssetSchema),
  ),
});

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const settings = context.get(settingsContext);
  const registrations = context.get(registrationContext);

  const pageData = {
    proofOfPurchaseAssets: registrations.suggested?.proofOfPurchaseAssets,
    uploadFileInfo: {
      storefrontId: settings.storefrontId,
      destination: 'crewProofOfPurchase' as const,
    },
  };

  return settings.registrations.isAutomatedRegistrationEnabled
    ? pageData
    : redirect('./customer-info');
});

export const { action, useActionResult } = makeAction(
  async ({ formData, context }, appStore) => {
    const settings = context.get(settingsContext);
    const registrations = context.get(registrationContext);
    const results = automatedFormSchema.safeParse(formData);

    if (!results.success) {
      return {
        ok: false as const,
        message:
          'Please upload at least one proof of purchase file before continuing.',
      };
    }

    const assets = results.data[AutomatedFormKeys.Assets];
    if (assets.length === 0) {
      return {
        ok: false as const,
        message: 'Please upload at least one file.',
      };
    }

    const proofOfPurchaseFiles = assets.map((asset) => ({
      src: asset.src,
      type: asset.type,
    }));

    const {
      shouldCollectAddress,
      customFields: offeredCustomFields,
      storeSalesChannels: offeredSalesChannels,
    } = settings.registrations;

    const suggestedReg = await api.analyzeRegistrationReceipt({
      params: { storefrontId: settings.storefrontId },
      body: {
        proofOfPurchaseFiles,
        shouldCollectAddress,
        offeredCustomFields,
        offeredSalesChannels,
      },
    });

    // Always store the suggested registration, preserving referrer from the loader
    appStore.dispatch(
      appStore.actions.registrations.setSuggested({
        ...registrations.suggested,
        ...suggestedReg,
        proofOfPurchaseAssets: assets,
      }),
    );

    return redirect('./customer-info');
  },
);
